import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { auth } from '../services/api';

function EyeIcon({ open }) {
  return open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function ResetPassword() {
  const [searchParams]            = useSearchParams();
  const navigate                  = useNavigate();
  const token                     = searchParams.get('token') || '';
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [showPw, setShowPw]       = useState(false);
  const [showCf, setShowCf]       = useState(false);
  const [focused, setFocused]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState(false);

  useEffect(() => {
    if (!token) setError('Invalid or missing reset link. Please request a new one.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) { setError('Please enter a new password.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setError('');
    setLoading(true);
    try {
      await auth.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const requirements = [
    { label: '8–12 characters',          ok: password.length >= 8 && password.length <= 12 },
    { label: 'One uppercase letter',      ok: /[A-Z]/.test(password) },
    { label: 'One lowercase letter',      ok: /[a-z]/.test(password) },
    { label: 'One number',               ok: /\d/.test(password) },
    { label: 'One special character',    ok: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]/.test(password) },
  ];

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#F9FAFB', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      padding: '1.5rem',
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{
        width: '100%', maxWidth: '420px',
        background: '#FFFFFF', borderRadius: '12px',
        boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
        overflow: 'hidden',
      }}>
        <div style={{ height: '3px', background: '#F97316' }} />

        <div style={{ padding: '2.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <img
              src="/btlogo.png" alt="BonTon"
              style={{ height: '32px', objectFit: 'contain', marginBottom: '1rem' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', margin: '0 0 0.3rem' }}>
              Set New Password
            </h1>
            <p style={{ color: '#6B7280', fontSize: '0.875rem', margin: 0 }}>
              Choose a strong password for your account.
            </p>
          </div>

          {success ? (
            <div style={{
              background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '0.5rem',
              padding: '1.25rem', textAlign: 'center',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
              <p style={{ color: '#15803D', fontSize: '0.9rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
                Password reset successful!
              </p>
              <p style={{ color: '#166534', fontSize: '0.82rem', margin: 0, lineHeight: 1.5 }}>
                Redirecting you to the login page...
              </p>
              <Link
                to="/login"
                style={{
                  display: 'inline-block', marginTop: '1.25rem',
                  color: '#F97316', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none',
                }}
              >
                Sign In Now →
              </Link>
            </div>
          ) : (
            <>
              {error && (
                <div style={{
                  background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem',
                  padding: '0.75rem 1rem', marginBottom: '1.25rem',
                  color: '#DC2626', fontSize: '0.82rem', lineHeight: 1.5,
                }}>
                  {error}
                </div>
              )}

              {!token ? (
                <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.85rem' }}>
                  <Link to="/forgot-password" style={{ color: '#F97316', fontWeight: 600, textDecoration: 'none' }}>
                    Request a new reset link →
                  </Link>
                </p>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* New Password */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#374151', marginBottom: '0.4rem' }}>
                      New Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPw ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter new password"
                        autoFocus
                        onFocus={() => setFocused('pw')}
                        onBlur={() => setFocused(null)}
                        style={{
                          width: '100%', boxSizing: 'border-box',
                          padding: '0.72rem 2.75rem 0.72rem 1rem',
                          border: `1.5px solid ${focused === 'pw' ? '#F97316' : '#E5E7EB'}`,
                          borderRadius: '0.5rem', fontSize: '0.9rem', color: '#111827',
                          background: '#FFFFFF', outline: 'none', transition: 'border-color 0.15s',
                          boxShadow: focused === 'pw' ? '0 0 0 3px rgba(249,115,22,0.1)' : 'none',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw(!showPw)}
                        style={{
                          position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF',
                          display: 'flex', alignItems: 'center', padding: 0,
                        }}
                      >
                        <EyeIcon open={showPw} />
                      </button>
                    </div>
                  </div>

                  {/* Password requirements */}
                  {password && (
                    <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#F9FAFB', borderRadius: '0.5rem' }}>
                      {requirements.map((r, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: i < requirements.length - 1 ? '0.3rem' : 0 }}>
                          <span style={{ fontSize: '0.75rem', color: r.ok ? '#10B981' : '#9CA3AF' }}>
                            {r.ok ? '✓' : '○'}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: r.ok ? '#065F46' : '#6B7280' }}>{r.label}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Confirm Password */}
                  <div style={{ marginBottom: '1.75rem' }}>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#374151', marginBottom: '0.4rem' }}>
                      Confirm Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showCf ? 'text' : 'password'}
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="Confirm new password"
                        onFocus={() => setFocused('cf')}
                        onBlur={() => setFocused(null)}
                        style={{
                          width: '100%', boxSizing: 'border-box',
                          padding: '0.72rem 2.75rem 0.72rem 1rem',
                          border: `1.5px solid ${focused === 'cf' ? '#F97316' : confirm && confirm !== password ? '#EF4444' : '#E5E7EB'}`,
                          borderRadius: '0.5rem', fontSize: '0.9rem', color: '#111827',
                          background: '#FFFFFF', outline: 'none', transition: 'border-color 0.15s',
                          boxShadow: focused === 'cf' ? '0 0 0 3px rgba(249,115,22,0.1)' : 'none',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCf(!showCf)}
                        style={{
                          position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF',
                          display: 'flex', alignItems: 'center', padding: 0,
                        }}
                      >
                        <EyeIcon open={showCf} />
                      </button>
                    </div>
                    {confirm && confirm !== password && (
                      <p style={{ fontSize: '0.75rem', color: '#EF4444', margin: '0.3rem 0 0' }}>Passwords do not match</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%', padding: '0.8rem',
                      background: loading ? '#FDBA74' : '#F97316',
                      color: '#FFFFFF', border: 'none', borderRadius: '0.5rem',
                      fontSize: '0.9rem', fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#EA580C'; }}
                    onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#F97316'; }}
                  >
                    {loading ? (
                      <>
                        <span style={{
                          width: '16px', height: '16px', flexShrink: 0,
                          border: '2px solid rgba(255,255,255,0.4)',
                          borderTop: '2px solid white', borderRadius: '50%',
                          animation: 'spin 0.75s linear infinite', display: 'inline-block',
                        }} />
                        Resetting...
                      </>
                    ) : 'Reset Password'}
                  </button>
                </form>
              )}

              <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.85rem' }}>
                <Link to="/login" style={{ color: '#F97316', fontWeight: 600, textDecoration: 'none' }}>
                  ← Back to Sign In
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
